import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom'
import EnvBanner from './components/EnvBanner'

const Login = lazy(() => import('./pages/Login'))
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'))
const ResetPassword = lazy(() => import('./pages/ResetPassword'))
const Questionnaire = lazy(() => import('./pages/Questionnaire'))
const Results = lazy(() => import('./pages/Results'))
const SharedRecommendation = lazy(() => import('./pages/SharedRecommendation'))
const SharedQuote = lazy(() => import('./pages/SharedQuote'))
const SharedPromotion = lazy(() => import('./pages/SharedPromotion'))
const Capture = lazy(() => import('./pages/Capture'))
const ConsultantSignup = lazy(() => import('./pages/ConsultantSignup'))
const PortalLayout = lazy(() => import('./components/PortalLayout'))
const NotFound = lazy(() => import('./components/NotFound'))
const Dashboard = lazy(() => import('./pages/ordering/Dashboard'))
const FarmerList = lazy(() => import('./pages/ordering/FarmerList'))
const FarmerView = lazy(() => import('./pages/ordering/FarmerView'))
const DryFertility = lazy(() => import('./pages/DryFertility'))
const ConsultantList = lazy(() => import('./pages/ordering/ConsultantList'))
const ConsultantView = lazy(() => import('./pages/ordering/ConsultantView'))
const TeamView = lazy(() => import('./pages/ordering/TeamView'))
const Analytics = lazy(() => import('./pages/ordering/Analytics'))
const Salespeople = lazy(() => import('./pages/ordering/Salespeople'))
const QuoteList = lazy(() => import('./pages/ordering/QuoteList'))
const QuoteCreate = lazy(() => import('./pages/ordering/QuoteCreate'))
const Remittance = lazy(() => import('./pages/ordering/Remittance'))
const MyRemittance = lazy(() => import('./pages/ordering/MyRemittance'))
const QuoteView = lazy(() => import('./pages/ordering/QuoteView'))
const OrderList = lazy(() => import('./pages/ordering/OrderList'))
const OrderCreate = lazy(() => import('./pages/ordering/OrderCreate'))
const OrderView = lazy(() => import('./pages/ordering/OrderView'))
const BulkTracker = lazy(() => import('./pages/ordering/BulkTracker'))
const OrderingProducts = lazy(() => import('./pages/ordering/OrderingProducts'))
const ProductView = lazy(() => import('./pages/ordering/ProductView'))
const DataHub = lazy(() => import('./pages/ordering/DataHub'))
const ConsultantProfile = lazy(() => import('./pages/ordering/ConsultantProfile'))
const UsersSecurity = lazy(() => import('./pages/ordering/UsersSecurity'))
const DistributorsView = lazy(() => import('./pages/ordering/DistributorsView'))
const DistributorDetailView = lazy(() => import('./pages/ordering/DistributorDetailView'))
const UserManage = lazy(() => import('./pages/ordering/UserManage'))
const BugReports = lazy(() => import('./pages/ordering/BugReports'))
const Announcements = lazy(() => import('./pages/ordering/Announcements'))
const MyReports = lazy(() => import('./pages/ordering/MyReports'))
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'))
const TeamReports = lazy(() => import('./pages/ordering/TeamReports'))
const RecommendationList = lazy(() => import('./pages/ordering/RecommendationList'))
const RecommendationDetail = lazy(() => import('./pages/ordering/RecommendationDetail'))
const Settings = lazy(() => import('./pages/ordering/Settings'))
const Resources = lazy(() => import('./pages/ordering/Resources'))
const PromotionAgreement = lazy(() => import('./pages/ordering/PromotionAgreement'))
const HelpLayout = lazy(() => import('./pages/help/HelpLayout'))
const HelpIndex = lazy(() => import('./pages/help/HelpIndex'))
const HelpDoc = lazy(() => import('./pages/help/HelpDoc'))

// The old Promotions list is folded into Analytics, but Nick's submission emails
// deep-link here with ?open=<id>. Preserve that: send ?open to the agreement, else
// to the Analytics screen where the equipment-program section now lives.
function PromotionsRedirect() {
  const [params] = useSearchParams()
  const open = params.get('open')
  return <Navigate to={open ? `/portal/promotions/${open}` : '/portal/analytics'} replace />
}

// Consultant Signups folded into Analytics; the onboarding notify email deep-links
// to /portal/consultant-signups?open=<id>. Preserve it: forward to the Analytics
// tab, carrying ?open through so the row opens.
function ConsultantSignupsRedirect() {
  const [params] = useSearchParams()
  const open = params.get('open')
  const to = `/portal/analytics?tab=consultant-signups${open ? `&open=${open}` : ''}`
  return <Navigate to={to} replace />
}

function App() {
  return (
    <div className="min-h-dvh bg-white">
      <EnvBanner />
      <Suspense fallback={null}>
        <Routes>
          {/* Root redirects to login */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<Navigate to="/forgot-password" replace />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />

          {/* Public read-only recommendation view — for sharing with farmers */}
          <Route path="/r/:sessionId" element={<SharedRecommendation />} />
          <Route path="/q/:quoteId" element={<SharedQuote />} />
          {/* Public remote signing for equipment-program agreements — matches the
              sign_path issued by POST /api/promotions/{id}/sign-request. */}
          <Route path="/promotions/:promotionId/sign" element={<SharedPromotion />} />
          <Route path="/capture/:token" element={<Capture />} />
          {/* Public RSM consultant-onboarding: fill + sign the Yield Consultant
              Agreement. Matches join_path from /api/consultant-signup-links. */}
          <Route path="/join/:token" element={<ConsultantSignup />} />

          {/* Public CAN-SPAM unsubscribe — reached from the release-email footer link */}
          <Route path="/unsubscribe" element={<Unsubscribe />} />

          {/* Public help center — shareable URLs, no login required */}
          <Route path="/help" element={<HelpLayout />}>
            <Route index element={<HelpIndex />} />
            <Route path=":slug" element={<HelpDoc />} />
          </Route>

          {/* Portal routes (auth + sidebar via PortalLayout) */}
          <Route path="/portal" element={<PortalLayout />}>
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="questionnaire" element={<Questionnaire />} />
            <Route path="questionnaire/:sessionId" element={<Questionnaire />} />
            <Route path="results/:sessionId" element={<Results />} />
            <Route path="recommendations" element={<RecommendationList />} />
            <Route path="recommendations/:sessionId" element={<RecommendationDetail />} />
            <Route path="farmers" element={<FarmerList />} />
            <Route path="farmers/new" element={<FarmerView />} />
            <Route path="farmers/:farmerId" element={<FarmerView />} />
            <Route path="farmers/:farmerId/dry-fertility" element={<DryFertility />} />
            <Route path="consultants" element={<ConsultantList />} />
            <Route path="consultants/new" element={<ConsultantView />} />
            <Route path="consultants/:consultantId" element={<ConsultantView />} />
            <Route path="team" element={<TeamView />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="pipeline" element={<Navigate to="/portal/analytics" replace />} />
            <Route path="prospects" element={<Navigate to="/portal/analytics" replace />} />
            <Route path="salespeople" element={<Salespeople />} />
            <Route path="quotes" element={<QuoteList />} />
            <Route path="quotes/create" element={<QuoteCreate />} />
            <Route path="quotes/:quoteId" element={<QuoteView />} />
            <Route path="promotions" element={<PromotionsRedirect />} />
            <Route path="promotions/new" element={<PromotionAgreement />} />
            <Route path="promotions/:promotionId" element={<PromotionAgreement />} />
            <Route path="remittance" element={<Remittance />} />
            <Route path="my-remittance" element={<MyRemittance />} />
            <Route path="orders" element={<OrderList />} />
            <Route path="orders/new" element={<OrderCreate />} />
            <Route path="orders/bulk" element={<BulkTracker />} />
            <Route path="orders/:orderId" element={<OrderView />} />
            <Route path="products" element={<OrderingProducts />} />
            <Route path="products/:productId" element={<ProductView />} />
            <Route path="reports" element={<Navigate to="/portal/data" replace />} />
            <Route path="profile" element={<ConsultantProfile />} />
            <Route path="users" element={<UsersSecurity />} />
            <Route path="users/:userId" element={<UserManage />} />
            <Route path="distributors" element={<DistributorsView />} />
            <Route path="distributors/:distributorId" element={<DistributorDetailView />} />
            <Route path="data" element={<DataHub />} />
            <Route path="admin" element={<Navigate to="/portal/data?tab=imports" replace />} />
            <Route path="bugs" element={<BugReports />} />
            <Route path="announcements" element={<Announcements />} />
            <Route path="my-reports" element={<MyReports />} />
            <Route path="team-reports" element={<TeamReports />} />
            <Route path="settings" element={<Settings />} />
            <Route path="resources" element={<Resources />} />
            {/* Consultant Signups lives as an Analytics tab now; preserve the
                notify-email deep link (?open=<id>) by forwarding it there. */}
            <Route path="consultant-signups" element={<ConsultantSignupsRedirect />} />
            {/* Unmatched /portal/* path — keeps the sidebar chrome */}
            <Route path="*" element={<NotFound />} />
          </Route>
          {/* Top-level catch-all: any other unmatched route (e.g. a stale
              /results/:id link) shows Not Found instead of a blank screen */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </div>
  )
}

export default App
